/**
 * 관제 WHEP getStats → 서버 배치 보고 (brand-manager 관제 구간).
 * 실패는 조용히 무시 — 재생 경로에 영향 없음.
 */

import { axiosInstance } from './axiosInstance';

export type WhepViewerSamplePayload = {
  pool_id: number;
  cctv_id: number;
  packets_lost: number;
  packets_received: number;
  frames_dropped: number;
  frames_decoded: number;
  freeze_count: number;
  jitter_ms: number;
  recv_kbps: number;
  interval_sec: number;
};

const MAX_QUEUE = 80;
const FLUSH_MS = 10000;

let queue: WhepViewerSamplePayload[] = [];
let flushTimer: number | null = null;
let flushing = false;

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushNow();
  }, FLUSH_MS);
}

async function flushNow() {
  if (flushing || queue.length === 0) return;
  flushing = true;
  const batch = queue.splice(0, MAX_QUEUE);
  try {
    await axiosInstance.post('/api/central-ai-monitor/whep-viewer-stats', {
      samples: batch,
    });
  } catch {
    /* ignore — 재생 방해 금지 */
  } finally {
    flushing = false;
    if (queue.length > 0) scheduleFlush();
  }
}

export function enqueueWhepViewerSample(sample: WhepViewerSamplePayload) {
  if (!sample || sample.pool_id <= 0 || sample.cctv_id <= 0) return;
  queue.push(sample);
  if (queue.length > MAX_QUEUE) {
    queue = queue.slice(-MAX_QUEUE);
  }
  scheduleFlush();
}

type Cumulative = {
  packetsLost: number;
  packetsReceived: number;
  framesDropped: number;
  framesDecoded: number;
  freezeCount: number;
  bytesReceived: number;
};

function emptyCum(): Cumulative {
  return {
    packetsLost: 0,
    packetsReceived: 0,
    framesDropped: 0,
    framesDecoded: 0,
    freezeCount: 0,
    bytesReceived: 0,
  };
}

async function readInboundVideo(pc: RTCPeerConnection): Promise<{
  cum: Cumulative;
  jitterSec: number;
}> {
  const cum = emptyCum();
  let jitterSec = 0;
  try {
    const report = await pc.getStats();
    report.forEach((r) => {
      const kind = (r as { kind?: string; mediaType?: string }).kind
        || (r as { mediaType?: string }).mediaType;
      if (r.type !== 'inbound-rtp') return;
      if (kind && kind !== 'video') return;
      // kind 없는 구형: 비디오 필드 있으면 채택
      const framesDecoded = Number((r as { framesDecoded?: number }).framesDecoded || 0);
      const hasVideoHint =
        framesDecoded > 0
        || typeof (r as { framesDropped?: number }).framesDropped === 'number'
        || typeof (r as { frameWidth?: number }).frameWidth === 'number';
      if (kind !== 'video' && !hasVideoHint) return;
      cum.packetsLost += Number((r as { packetsLost?: number }).packetsLost || 0);
      cum.packetsReceived += Number((r as { packetsReceived?: number }).packetsReceived || 0);
      cum.framesDropped += Number((r as { framesDropped?: number }).framesDropped || 0);
      cum.framesDecoded += framesDecoded;
      cum.freezeCount += Number((r as { freezeCount?: number }).freezeCount || 0);
      cum.bytesReceived += Number((r as { bytesReceived?: number }).bytesReceived || 0);
      const j = Number((r as { jitter?: number }).jitter || 0);
      if (j > jitterSec) jitterSec = j;
    });
  } catch {
    /* ignore */
  }
  return { cum, jitterSec };
}

/**
 * PC 연결 동안 주기 getStats → 증분 enqueue. 반환 cleanup.
 */
export function startWhepViewerStatsLoop(
  pc: RTCPeerConnection,
  poolId: number,
  cctvId: number,
  intervalMs = 5000
): () => void {
  let prev: Cumulative | null = null;
  let prevAt = 0;
  let stopped = false;
  let timer: number | null = null;

  const tick = async () => {
    if (stopped) return;
    if (pc.connectionState === 'closed' || pc.connectionState === 'failed') return;
    const now = Date.now();
    const { cum, jitterSec } = await readInboundVideo(pc);
    if (stopped) return;
    if (prev != null && prevAt > 0) {
      const dtSec = Math.max(0.2, (now - prevAt) / 1000);
      const lost = Math.max(0, cum.packetsLost - prev.packetsLost);
      const recv = Math.max(0, cum.packetsReceived - prev.packetsReceived);
      const dropped = Math.max(0, cum.framesDropped - prev.framesDropped);
      const decoded = Math.max(0, cum.framesDecoded - prev.framesDecoded);
      const freeze = Math.max(0, cum.freezeCount - prev.freezeCount);
      const bytes = Math.max(0, cum.bytesReceived - prev.bytesReceived);
      const recvKbps = (bytes * 8) / 1000 / dtSec;
      enqueueWhepViewerSample({
        pool_id: poolId,
        cctv_id: cctvId,
        packets_lost: lost,
        packets_received: recv,
        frames_dropped: dropped,
        frames_decoded: decoded,
        freeze_count: freeze,
        jitter_ms: Math.round(jitterSec * 1000 * 100) / 100,
        recv_kbps: Math.round(recvKbps * 10) / 10,
        interval_sec: Math.round(dtSec * 100) / 100,
      });
    }
    prev = cum;
    prevAt = now;
  };

  void tick();
  timer = window.setInterval(() => {
    void tick();
  }, intervalMs);

  return () => {
    stopped = true;
    if (timer != null) {
      window.clearInterval(timer);
      timer = null;
    }
  };
}
